import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Radio, Tag, Tooltip } from 'antd';
import { ze } from 'utils/zhEn';

const LEVEL_RANK = {
  V: 0,
  D: 1,
  I: 2,
  W: 3,
  A: 4,
  E: 5,
  F: 6,
  Ex: 6,
};

const LEVEL_COLOR = {
  V: 'default',
  D: 'default',
  I: 'blue',
  W: 'orange',
  A: 'red',
  E: 'red',
  F: 'magenta',
  Ex: 'magenta',
};

const LEVEL_LABEL = {
  V: 'VERBOSE',
  D: 'DEBUG',
  I: 'INFO',
  W: 'WARN',
  A: 'ASSERT',
  E: 'ERROR',
  F: 'FATAL',
  Ex: 'EXCEPTION',
};

const SOURCE_META = {
  sysLog: { zh: '系统 logcat', en: 'System logcat', color: 'default' },
  userLog: { zh: '自定义日志', en: 'Custom Log', color: 'geekblue' },
  extraMessage: { zh: '引擎回调日志', en: 'Engine ExtraLog', color: 'purple' },
  martianLog: { zh: '前后台切换', en: 'App Lifecycle', color: 'cyan' },
};

function cleanControlChars(raw) {
  return String(raw || '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]+/g, '')
    .trim();
}

export function parseAndroidLogLine(rawLine, source = 'sysLog', index = 0, refDate = { year: 2026, month: 10, day: 19 }) {
  const line = cleanControlChars(rawLine);
  if (!line) return null;
  if (/^-+\s*beginning of\s+/i.test(line)) {
    return null;
  }
  const refYear = Number(refDate?.year) || 2026;
  const refMonth = Number(refDate?.month) || 10;
  const refDay = Number(refDate?.day) || 19;

  // 1. UE4/UE5 extraMessage format: [YYYY.MM.DD-HH.mm.ss:SSS][frame](optional [World_01])Category: Verbosity: Message
  const ueRe =
    /^\[(\d{4})\.(\d{2})\.(\d{2})-(\d{2})\.(\d{2})\.(\d{2}):(\d{1,3})\](?:\[\s*(\d+)\])?(?:\[([^\]]+)\])?\s*([^:]+?):\s*(Fatal|Error|Warning|Display|Log|Verbose|VeryVerbose)?\s*:?\s*(.*)$/i;
  const ueMatch = line.match(ueRe);
  if (ueMatch) {
    const [, y, m, d, hh, mm, ss, msRaw, frameNo, worldTag, catRaw, verbRaw, msgRaw] = ueMatch;
    const body = (msgRaw || '').trim();
    if (!body && !/ErrExpDesc/i.test(line)) {
      return null;
    }
    if (/^\[Callstack\]/i.test(body) || /^===\s*Critical error/i.test(body)) {
      return null;
    }
    const ms = Number(String(msRaw).padEnd(3, '0'));
    const tsMs = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    const verb = String(verbRaw || 'Log').toLowerCase();
    let levelAbbr = 'I';
    if (verb === 'fatal') levelAbbr = 'F';
    else if (verb === 'error') levelAbbr = 'E';
    else if (verb === 'warning') levelAbbr = 'W';
    else if (verb === 'verbose' || verb === 'veryverbose') levelAbbr = 'V';
    const frameLabel = frameNo
      ? `Frame#${frameNo}${worldTag ? ` (${worldTag.trim()})` : ''}`
      : worldTag
      ? worldTag.trim()
      : undefined;
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}.${String(ms).padStart(3, '0')}`,
      pidText: frameLabel,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag: catRaw.trim(),
      message: body || catRaw.trim(),
      extraLines: [],
    };
  }

  // 1b. PC PrintLog with embedded UE timestamp: "10-19 21:40:06 I Monitor [2026.10.19-21.40.06:430][177] PostLoadMap: ..."
  const pcUeEmbedRe =
    /^(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:[.,](\d{1,3}))?\s+([VDIWEAF])\s+([^\s[]+)\s+\[(\d{4})\.(\d{2})\.(\d{2})-(\d{2})\.(\d{2})\.(\d{2}):(\d{1,3})\](?:\[\s*(\d+)\])?\s*(.*)$/i;
  const pcUeMatch = line.match(pcUeEmbedRe);
  if (pcUeMatch) {
    const [, , , , , , , lvl, tagRaw, y, m, d, hh, mm, ss, msRaw, frameNo, msgRaw] = pcUeMatch;
    const ms = Number(String(msRaw).padEnd(3, '0'));
    const tsMs = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    const levelAbbr = lvl.toUpperCase();
    const body = (msgRaw || '').trim();
    const isCmd = /^Cmd:\s+/i.test(body);
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}.${String(ms).padStart(3, '0')}`,
      pidText: frameNo ? `Frame#${frameNo}` : undefined,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag: isCmd ? `${tagRaw.trim()}:Cmd` : tagRaw.trim(),
      isConsoleCmdBatch: isCmd,
      message: body || line,
      extraLines: [],
    };
  }

  // 2. martianlog format: "YYYY-MM-DD HH:mm:ss  com.pkg.Activity  onResumed"
  const martianRe =
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})\s+([^\s]+)\s+(on\w+.*)$/;
  const mMatch = line.match(martianRe);
  if (mMatch && source === 'martianLog') {
    const [, y, m, d, hh, mm, ss, actName, stateName] = mMatch;
    const tsMs = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), 0);
    const shortAct = actName.split('.').slice(-1)[0] || actName;
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}`,
      levelAbbr: 'I',
      levelRank: 2,
      tag: shortAct,
      message: `${actName} → ${stateName.trim()}`,
      extraLines: [],
    };
  }

  // 3. Standard YYYY-MM-DD HH:mm:ss(.SSS) or YYYY/MM/DD HH:mm:ss(.SSS)
  const fullDateRe =
    /^\[?(\d{4})[-/](\d{2})[-/](\d{2})[T\s]+(\d{2}):(\d{2}):(\d{2})(?:[.,\s](\d{1,3}))?\]?\s*(.*)$/;
  const fullMatch = line.match(fullDateRe);
  if (fullMatch) {
    const [, y, m, d, hh, mm, ss, msRaw, rest] = fullMatch;
    const ms = msRaw ? Number(String(msRaw).padEnd(3, '0')) : 0;
    const tsMs = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    const levelMatch = rest.match(/^(?:\[?(V|D|I|W|E|A|F|INFO|WARN|WARNING|ERROR|DEBUG|FATAL)\]?[\s:|-]+)?(.*)$/i);
    let levelAbbr = 'I';
    let body = rest;
    if (levelMatch && levelMatch[1]) {
      const u = levelMatch[1].toUpperCase();
      levelAbbr = u === 'WARNING' ? 'W' : u.slice(0, 1);
      body = levelMatch[2] || rest;
    }
    const tagMatch = body.match(/^([A-Za-z0-9_.\-[\]]{2,36})\s*:\s*(.*)$/);
    const tag = tagMatch ? tagMatch[1] : SOURCE_META[source]?.en || 'Log';
    const message = tagMatch ? tagMatch[2] : body;
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}${msRaw ? `.${String(ms).padStart(3, '0')}` : ''}`,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag,
      message: message || body || line,
      extraLines: [],
    };
  }

  // 4. Android logcat / CrashSightPlugin userLog: MM-DD HH:mm:ss(.SSS) PID TID LEVEL TAG: message
  const logcatRe =
    /^(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:[.,](\d{1,3}))?\s+(\d+)\s+(\d+)\s+([VDIWEAF])\s+([^:]*?)\s*:\s*(.*)$/;
  const lcMatch = line.match(logcatRe);
  if (lcMatch) {
    const [, m, d, hh, mm, ss, msRaw, pidStr, tidStr, lvl, tagRaw, msg] = lcMatch;
    const body = (msg || '').trim();
    if (!body) {
      return null;
    }
    const ms = msRaw ? Number(String(msRaw).padEnd(3, '0')) : 0;
    const tsMs = Date.UTC(refYear, Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    const levelAbbr = lvl.toUpperCase();
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}${msRaw ? `.${String(ms).padStart(3, '0')}` : ''}`,
      pidText: `${pidStr}/${tidStr}`,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag: tagRaw.trim() || 'logcat',
      message: body,
      extraLines: [],
    };
  }

  // 4b. PC PrintLog / crashSightLog.log without PID/TID: "MM-DD HH:mm:ss(.SSS) LEVEL TAG message"
  const pcPrintLogRe =
    /^(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:[.,](\d{1,3}))?\s+([VDIWEAF])\s+([^\s:]+)\s*:?\s*(.*)$/;
  const pcPrintMatch = line.match(pcPrintLogRe);
  if (pcPrintMatch) {
    const [, m, d, hh, mm, ss, msRaw, lvl, tagRaw, msg] = pcPrintMatch;
    const ms = msRaw ? Number(String(msRaw).padEnd(3, '0')) : 0;
    const tsMs = Date.UTC(refYear, Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    const levelAbbr = lvl.toUpperCase();
    const body = (msg || '').trim();
    const isCmd = /^Cmd:\s+/i.test(body);
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${m}-${d} ${hh}:${mm}:${ss}${msRaw ? `.${String(ms).padStart(3, '0')}` : ''}`,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag: isCmd ? `${tagRaw.trim()}:Cmd` : tagRaw.trim(),
      isConsoleCmdBatch: isCmd,
      message: body || tagRaw.trim(),
      extraLines: [],
    };
  }

  // 5. Unity extraMessage time-only line: "(extmsg: )?HH:mm:ss(.SSS) message"
  const timeOnlyRe = /^(?:extmsg:\s*)?(\d{2}):(\d{2}):(\d{2})(?:[.,\s](\d{1,3})\])?\s+(.+)$/;
  const toMatch = line.match(timeOnlyRe);
  if (toMatch) {
    const [, hh, mm, ss, msRaw, rest] = toMatch;
    const ms = msRaw ? Number(String(msRaw).padEnd(3, '0')) : 0;
    const tsMs = Date.UTC(refYear, refMonth - 1, refDay, Number(hh), Number(mm), Number(ss), ms);
    const tagMatch = rest.match(/^([A-Za-z0-9_.\-[\]]{2,36})\s*:\s*(.*)$/);
    const tag = tagMatch ? tagMatch[1] : source === 'extraMessage' ? 'EngineExtra' : 'Log';
    const message = tagMatch ? tagMatch[2] : rest;
    return {
      index,
      source,
      rawLine: line,
      tsMs: Number.isFinite(tsMs) ? tsMs : null,
      timeText: `${String(refMonth).padStart(2, '0')}-${String(refDay).padStart(2, '0')} ${hh}:${mm}:${ss}`,
      levelAbbr: /error|exception|fatal/i.test(rest) ? 'E' : /warn/i.test(rest) ? 'W' : 'I',
      levelRank: /error|exception|fatal/i.test(rest) ? 5 : /warn/i.test(rest) ? 3 : 2,
      tag,
      message,
      extraLines: [],
    };
  }

  // 6. Relative boot seconds or truncated ring-buffer prefix line (e.g. "0.060 18518 28181 W libc : ..." or "973 2821 3592 D MNA-efb3808b: ...")
  const bootSecRe = /^(\d+(?:\.\d{1,6})?)\s+(\d+)\s+(\d+)\s+([VDIWEAF])\s+([^:]+?)\s*:\s*(.*)$/;
  const bootMatch = line.match(bootSecRe);
  if (bootMatch) {
    const [, secStr, pidStr, tidStr, lvl, tagRaw, msg] = bootMatch;
    const levelAbbr = lvl.toUpperCase();
    const isTruncatedMs = !secStr.includes('.');
    return {
      index,
      source,
      rawLine: line,
      tsMs: null,
      timeText: isTruncatedMs ? `~.${secStr.padStart(3, '0')}s` : `+${secStr}s`,
      pidText: `${pidStr}/${tidStr}`,
      levelAbbr,
      levelRank: LEVEL_RANK[levelAbbr] ?? 2,
      tag: tagRaw.trim(),
      message: msg.trim() || line,
      extraLines: [],
    };
  }

  // 7. Continuation / non-timestamped line
  return {
    index,
    source,
    rawLine: line,
    tsMs: null,
    isContinuation: true,
    timeText: `#${index}`,
    levelAbbr: 'I',
    levelRank: 2,
    tag: SOURCE_META[source]?.en || 'Log',
    message: line,
    extraLines: [],
  };
}

export function classifyTimelineSemanticTag(entry) {
  if (entry?.source === 'martianLog') {
    return { key: 'lifecycle', labelZh: '前后台切换', labelEn: 'App Lifecycle', color: 'cyan' };
  }
  const text = `${entry?.tag || ''} ${entry?.message || ''}`;
  if (
    /CrashSightReport-Native|ReportException|handleNativeException|unwind full native stack|Dumper::GenerateDump|get signal\s+\d+|Forwarding signal\s+\d+|VK_ERROR_DEVICE_LOST|Fatal error|SIGSEGV|SIGABRT|Fatal signal/i.test(
      text
    )
  ) {
    return { key: 'crash_signal', labelZh: '异常 / 崩溃信号', labelEn: 'Crash Signal', color: 'volcano' };
  }
  if (/\b(GC|sticky GC|maxfree|LowMemory|onTrimMemory|OutOfMemory|OOM|ClearPooledObjects|Large Bundle)\b/i.test(text)) {
    return { key: 'memory_gc', labelZh: '内存 / GC', labelEn: 'Memory / GC', color: 'purple' };
  }
  if (/\b(Form\.Open|Form\.Close|LoadMap|OnPreLoadMap|PostLoadMap|Settlement|Settle|StartGame|PostStart|onResumed|onPaused|Activity|Scene|Level|Change report stage|ReportAction|LevelAddedToWorld|RequestLevel)\b/i.test(text)) {
    return { key: 'lifecycle', labelZh: 'UI / 场景 / 生命周期', labelEn: 'UI / Scene', color: 'geekblue' };
  }
  if (/\b(MotionEvent|ACTION_DOWN|ACTION_UP|KeyEvent|Touch|onClick|Input|MouseLockMode|bShowMouseCursor)\b/i.test(text)) {
    return { key: 'user_input', labelZh: '点触 / 输入', labelEn: 'User Input', color: 'cyan' };
  }
  if (entry?.levelRank >= 5) {
    return { key: 'error', labelZh: '错误', labelEn: 'Error', color: 'red' };
  }
  if (entry?.levelRank === 3 || entry?.levelRank === 4) {
    return { key: 'warn', labelZh: '告警', labelEn: 'Warning', color: 'orange' };
  }
  return null;
}

export function extractAndroidAttachmentLogs(attachList = []) {
  if (!Array.isArray(attachList)) {
    return { martianLines: [], extraLogLines: [], snapshotKvs: [] };
  }
  const martianContent = attachList.find((a) => a && a.fileName === 'martianlog.txt')?.content || '';
  const extraContent =
    attachList.find(
      (a) =>
        a &&
        (a.fileName === 'extraMessage.txt' ||
          a.fileName === 'ExtraMessage.txt' ||
          a.fileName === 'crash_attach.log')
    )?.content || '';
  const kvContent =
    attachList.find(
      (a) => a && (a.fileName === 'valueMapOthers.txt' || a.fileName === 'CustomizedData.txt')
    )?.content || '';

  const martianLines = String(martianContent)
    .split('\n')
    .map((s) => cleanControlChars(s))
    .filter(Boolean);

  // Extract timestamped engine log lines from extraMessage.txt AND capture multi-line Fatal error / ErrExpDesc
  const rawExtraLines = String(extraContent)
    .split(/\r?\n/)
    .map((s) => cleanControlChars(s));
  const extraLogLines = [];
  let inFatalErrDesc = false;
  let inSkipBlock = false;
  for (let i = 0; i < rawExtraLines.length; i += 1) {
    const line = rawExtraLines[i];
    if (!line) continue;
    const isUeTs = /^\[(\d{4})\.(\d{2})\.(\d{2})-(\d{2})\.(\d{2})\.(\d{2}):(\d{1,3})\]/.test(line);
    const isUnityTs = /^(?:extmsg:\s*)?\d{2}:\d{2}:\d{2}\s+/.test(line);
    if (isUeTs || isUnityTs) {
      inFatalErrDesc = /\|ErrExpDesc:\s*$/i.test(line);
      inSkipBlock = /CrashInfoString:\s*\x7b/i.test(line);
      if (isUeTs && /\[Callstack\]|===\s*Critical error|OnCrashExtraMessageNotify/i.test(line)) {
        continue;
      }
      if (!inSkipBlock) {
        extraLogLines.push(line);
      }
      continue;
    }
    if (/^(?:end1|end===+|JSCallstack:|JsEnv\[\d+\]|@\s*[A-Za-z]:\\|at\s+[^\s]+\()/i.test(line)) {
      inFatalErrDesc = false;
      inSkipBlock = true;
      continue;
    }
    if (inSkipBlock) {
      continue;
    }
    if (inFatalErrDesc || /^Fatal error[:!]/i.test(line)) {
      extraLogLines.push(line);
      inFatalErrDesc = true;
    }
  }

  // Extract high-value PC/mobile scene & memory snapshot from valueMapOthers.txt (CustomizedData.txt)
  const snapshotKvs = [];
  if (kvContent) {
    const kvMap = {};
    String(kvContent)
      .split(/\r?\n/)
      .forEach((l) => {
        const eqIdx = l.indexOf('=');
        if (eqIdx > 0) {
          const k = l.slice(0, eqIdx).trim();
          const v = l.slice(eqIdx + 1).trim();
          if (k && v) kvMap[k] = v;
        }
      });
    if (kvMap['K#CurrentMapName']) {
      snapshotKvs.push({
        key: 'map',
        labelZh: `当前地图: ${kvMap['K#CurrentMapName']}`,
        labelEn: `Map: ${kvMap['K#CurrentMapName']}`,
        color: 'geekblue',
      });
    }
    if (kvMap['K#IsForeground'] === '0' || kvMap['K#IsForeground'] === '1') {
      const isFg = kvMap['K#IsForeground'] === '1';
      snapshotKvs.push({
        key: 'fg',
        labelZh: isFg ? '窗口状态: 前台运行' : '窗口状态: 后台运行',
        labelEn: isFg ? 'Window: Foreground' : 'Window: Background',
        color: isFg ? 'cyan' : 'orange',
      });
    }
    if (kvMap['K#MemoryStatsEnd']) {
      const memEnd = kvMap['K#MemoryStatsEnd'];
      const usedM = memEnd.match(/AppUsedPhysical:\s*(\d+\s*MB)/i)?.[1];
      const peakM = memEnd.match(/AppPeakUsedPhysical:\s*(\d+\s*MB)/i)?.[1];
      const availM = memEnd.match(/AvailablePhysical:\s*(\d+\s*MB)/i)?.[1];
      if (usedM || availM) {
        const memParts = [
          usedM ? `进程物理内存 ${usedM}${peakM ? ` (峰值 ${peakM})` : ''}` : null,
          availM ? `系统可用 ${availM}` : null,
        ].filter(Boolean);
        snapshotKvs.push({
          key: 'mem',
          labelZh: memParts.join(' · '),
          labelEn: memParts.join(' · '),
          color: 'purple',
        });
      }
    }
    if (kvMap['K#GameProcessExitCode']) {
      snapshotKvs.push({
        key: 'exitCode',
        labelZh: `进程退出码: ${kvMap['K#GameProcessExitCode']}`,
        labelEn: `ExitCode: ${kvMap['K#GameProcessExitCode']}`,
        color: 'volcano',
      });
    }
  }

  return { martianLines, extraLogLines, snapshotKvs };
}

function parseSourceLinesWithContinuation(lines = [], source, refDate) {
  const result = [];
  lines.forEach((raw, idx) => {
    const parsed = parseAndroidLogLine(raw, source, idx, refDate);
    if (!parsed) return;
    // Skip placeholder tombstone miss message
    if (/this crash is occurred at last process!\s*Log is miss/i.test(parsed.message)) {
      return;
    }
    // Skip Android logcat ring-buffer duplicate summary lines ("I chatty : uid=... identical N lines")
    if (String(parsed.tag || '').trim() === 'chatty' && /\bidentical\s+\d+\s+lines?\b/i.test(parsed.message)) {
      return;
    }
    // Fold timestamped stack/continuation lines (Java/JS at ..., Unity #00 pc ..., [ line ...], Escher # moduleName / @ ...) into the previous same-tag event
    const isNativeCrashDumpTag = /^(?:CRASH|DEBUG|AndroidRuntime|libc|tombstoned|\[?CrashSightReport(?:-Native)?\]?)$/i.test(
      String(parsed.tag || '').trim()
    );
    if (
      !isNativeCrashDumpTag &&
      /^(?:#\d{2}\s+pc\s+[0-9a-f]+|\[ line [-\d]+\]|at\s+\S+.*\(.*\)|Caused by:\s+.+|\.\.\.\s+\d+\s+more|#\s*moduleName:|@\s+\S+)/i.test(
        parsed.message
      )
    ) {
      const prev = result[result.length - 1];
      if (prev && prev.tag === parsed.tag) {
        prev.extraLines.push(parsed.message);
        return;
      }
    }
    if (parsed.isContinuation) {
      // Fold continuation lines (e.g. ***heroID, *********, #00 pc ..., Fatal error: ...) into the previous event of the same source
      const prev = result[result.length - 1];
      if (prev) {
        if (prev.isErrExpDesc || /\|ErrExpDesc:/i.test(prev.message) || /^Fatal error[:!]/i.test(parsed.message)) {
          prev.isErrExpDesc = true;
          prev.message = `${prev.message} ${parsed.message}`.trim();
          prev.levelAbbr = 'E';
          prev.levelRank = 5;
          return;
        }
        if (!/^(\*{3,})$/.test(parsed.message)) {
          prev.extraLines.push(parsed.message);
        }
        return;
      }
    }
    result.push(parsed);
  });
  return result;
}

function normalizeMessageTemplate(msg) {
  const raw = String(msg || '').trim();
  if (/^Cmd:\s+/i.test(raw)) {
    return 'cmd:#cvar_batch';
  }
  return raw
    .toLowerCase()
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '#uuid')
    .replace(/"[^"]+"/g, '"#str"')
    .replace(/(msg:\s*)\S+/gi, '$1#payload')
    .replace(/^(loadbank(?:async)?:\s*)\S+/i, '$1#bank')
    .replace(/^(postevent:\s*)\S+/i, '$1#event')
    .replace(/^(form\.(?:open|close):\s*)\S+/i, '$1#form')
    .replace(/0x[0-9a-f]+/gi, '#hex')
    .replace(/\b[0-9a-f]{16,}\b/gi, '#hex')
    .replace(/(actor:\s*)[^,|]+/gi, '$1#actor')
    .replace(/(owner=\s*)[^\s,|]+/gi, '$1#owner')
    .replace(/(packageid=\s*)[0-9a-f]+/gi, '$1#pkg')
    .replace(/\b\d+(\.\d+)?\b/g, '#n')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

function isCrashDumpTagOrSignal(entry) {
  if (!entry) return false;
  const tag = String(entry.tag || '').trim();
  const msg = String(entry.message || '').trim();
  if (/^(CRASH|DEBUG|\[?CrashSightReport-Native\]?)$/i.test(tag)) {
    return true;
  }
  if (/^\[?CrashSightReport\]?$/i.test(tag)) {
    return /ReportException|handleNativeException|get signal|GenerateDump|unwind full native stack/i.test(
      msg
    );
  }
  if (
    /^(?:\*\*\*\s*\*\*\*|signal\s+\d+\s*\(|Fatal signal\s+\d+|backtrace:|Tombstone written to:|Forwarding signal\s+\d+)/i.test(
      msg
    ) ||
    (/^(?:CRASH|DEBUG|AndroidRuntime|libc|tombstoned)$/i.test(tag) &&
      /^#\d{2}\s+pc\s+[0-9a-f]+/i.test(msg)) ||
    /\|ErrExpDesc:|Fatal error[:!]|HandleWindowsCrash|PreCrashCallBack/i.test(msg)
  ) {
    return true;
  }
  return false;
}

export function buildCrashTimelineEvents({
  sysLogs = [],
  userLogs = [],
  attachList = [],
  allowSysLog = true,
  allowUserLog = false,
  crashTime = '',
  keyword = '',
  minLevelName = 'verbose',
  windowSec = 30,
  categoryFilter = 'all',
}) {
  const minRankMap = {
    verbose: 0,
    debug: 1,
    info: 2,
    warn: 3,
    assert: 4,
    error: 5,
    exception: 6,
  };
  const minRank = minRankMap[String(minLevelName || 'verbose').toLowerCase()] ?? 0;
  const kw = String(keyword || '').trim().toLowerCase();

  let refDate = { year: 2026, month: 10, day: 20 };
  const ctMatch = String(crashTime || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ctMatch) {
    refDate = {
      year: Number(ctMatch[1]),
      month: Number(ctMatch[2]),
      day: Number(ctMatch[3]),
    };
  } else if (/^\d{10,13}$/.test(String(crashTime || '').trim())) {
    const rawNum = Number(String(crashTime).trim());
    const epochMs = rawNum < 1e11 ? rawNum * 1000 : rawNum;
    // Convert UTC epoch to UTC+8 date components for China logs
    const bj = new Date(epochMs + 8 * 3600 * 1000);
    if (Number.isFinite(bj.getTime())) {
      refDate = {
        year: bj.getUTCFullYear(),
        month: bj.getUTCMonth() + 1,
        day: bj.getUTCDate(),
      };
    }
  }

  const { martianLines, extraLogLines, snapshotKvs } = extractAndroidAttachmentLogs(attachList);

  const merged = [];
  if (allowSysLog && Array.isArray(sysLogs)) {
    merged.push(...parseSourceLinesWithContinuation(sysLogs, 'sysLog', refDate));
  }
  if (allowUserLog && Array.isArray(userLogs)) {
    merged.push(...parseSourceLinesWithContinuation(userLogs, 'userLog', refDate));
  }
  if (allowUserLog && extraLogLines.length > 0) {
    merged.push(...parseSourceLinesWithContinuation(extraLogLines, 'extraMessage', refDate));
  }
  if (martianLines.length > 0) {
    merged.push(...parseSourceLinesWithContinuation(martianLines, 'martianLog', refDate));
  }

  // Sort chronologically by tsMs; preserve source order when tsMs is equal
  merged.sort((a, b) => {
    if (a.tsMs != null && b.tsMs != null) {
      return a.tsMs - b.tsMs || a.index - b.index;
    }
    if (a.tsMs == null && b.tsMs != null) return -1;
    if (a.tsMs != null && b.tsMs == null) return 1;
    return a.index - b.index;
  });

  const withTs = merged.filter((e) => e.tsMs != null);
  let lastCrashBlockStartTsMs = null;
  let inCrashBlock = false;
  let prevCrashTsMs = null;
  for (let i = 0; i < withTs.length; i += 1) {
    const item = withTs[i];
    if (isCrashDumpTagOrSignal(item)) {
      if (!inCrashBlock || (prevCrashTsMs != null && item.tsMs - prevCrashTsMs > 1500)) {
        lastCrashBlockStartTsMs = item.tsMs;
        inCrashBlock = true;
      }
      prevCrashTsMs = item.tsMs;
    } else if (prevCrashTsMs != null && item.tsMs - prevCrashTsMs > 1500) {
      inCrashBlock = false;
    }
  }
  let crashTimeAnchorMs = null;
  const fullCrashTimeMatch = String(crashTime || '').match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:[\s.:](\d{1,3}))?/
  );
  if (fullCrashTimeMatch) {
    const [, cy, cm, cd, ch, cmin, cs, cmsRaw] = fullCrashTimeMatch;
    const cms = cmsRaw ? Number(String(cmsRaw).padEnd(3, '0')) : 0;
    const parsedCt = Date.UTC(
      Number(cy),
      Number(cm) - 1,
      Number(cd),
      Number(ch),
      Number(cmin),
      Number(cs),
      cms
    );
    if (Number.isFinite(parsedCt)) {
      crashTimeAnchorMs = parsedCt;
    }
  }
  const maxLogTsMs = withTs.length > 0 ? Math.max(...withTs.map((e) => e.tsMs)) : null;
  const anchorMs =
    lastCrashBlockStartTsMs != null
      ? lastCrashBlockStartTsMs
      : crashTimeAnchorMs != null &&
        maxLogTsMs != null &&
        Math.abs(maxLogTsMs - crashTimeAnchorMs) <= 60 * 1000
      ? Math.min(maxLogTsMs, crashTimeAnchorMs)
      : maxLogTsMs;

  const enriched = merged.map((e) => {
    const rawDiffSec = anchorMs != null && e.tsMs != null ? (anchorMs - e.tsMs) / 1000 : null;
    const isPostCrash = rawDiffSec != null && rawDiffSec < -0.004;
    const deltaSec = rawDiffSec != null ? Math.max(0, rawDiffSec) : null;
    const postSec = isPostCrash ? Math.abs(rawDiffSec) : 0;
    const deltaLabel =
      rawDiffSec == null
        ? e.timeText
        : isPostCrash
        ? `T+${postSec.toFixed(2)}s`
        : `T-${deltaSec.toFixed(2)}s`;
    const isCrashDump = isCrashDumpTagOrSignal(e);
    const semantic = isCrashDump
      ? { key: 'crash_signal', labelZh: '异常 / 崩溃信号', labelEn: 'Crash Signal', color: 'volcano' }
      : classifyTimelineSemanticTag(e);
    return {
      ...e,
      deltaSec,
      isPostCrash,
      deltaLabel,
      isCrashDump,
      msgTemplate: normalizeMessageTemplate(e.message),
      semantic,
    };
  });

  const matchesCategoryAndKeyword = (e) => {
    if (e.levelRank < minRank) return false;
    if (kw) {
      const fullText = `${e.rawLine} ${(e.extraLines || []).join(' ')}`.toLowerCase();
      if (!fullText.includes(kw)) return false;
    }
    if (categoryFilter === 'warn_error') {
      return e.levelRank >= 3 || (e.semantic && e.semantic.key === 'crash_signal');
    }
    if (categoryFilter === 'signals') {
      return !!e.semantic || e.source === 'userLog' || e.source === 'martianLog';
    }
    if (categoryFilter === 'custom_engine') {
      return e.source === 'userLog' || e.source === 'extraMessage' || e.source === 'martianLog';
    }
    if (categoryFilter === 'sysLog') {
      return e.source === 'sysLog';
    }
    return true;
  };

  const baseMatched = enriched.filter(matchesCategoryAndKeyword);
  const hiddenEarlierItems =
    windowSec > 0
      ? baseMatched.filter((e) => e.deltaSec != null && e.deltaSec > windowSec)
      : [];
  let filtered = baseMatched.filter((e) => {
    if (windowSec > 0 && e.deltaSec != null && e.deltaSec > windowSec) {
      return false;
    }
    return true;
  });

  // If 30s window has 0 events but earlier events exist, auto-include the most recent 25 events and mark autoExpandedWindow
  let autoExpandedWindow = false;
  if (windowSec > 0 && filtered.length === 0 && baseMatched.length > 0) {
    filtered = baseMatched.slice(-25);
    autoExpandedWindow = true;
  }
  const hiddenEarlierCount = autoExpandedWindow ? 0 : hiddenEarlierItems.length;
  const hiddenEarlierSysLogCount = autoExpandedWindow
    ? 0
    : hiddenEarlierItems.filter((e) => e.source === 'sysLog').length;

  // Fold consecutive events:
  // 1) Consecutive Native crash dump / CrashSightReport lines fold into a single collapsible Crash Dump block
  // 2) Normal events fold when source + tag (or Escher-*) + levelAbbr + msgTemplate + extraLines signature match and time gap <= 2s
  const groups = [];
  filtered.forEach((item) => {
    const semKey = item.semantic?.key || 'plain';
    const prev = groups[groups.length - 1];
    if (prev && prev.isCrashDumpGroup && item.isCrashDump && prev.source === item.source) {
      prev.items.push(item);
      prev.endDeltaLabel = item.deltaLabel;
      const summaryCandidate =
        prev.items.find((x) =>
          /signal\s+\d+\s*\(|Fatal signal|Abort message|get signal|unwind full native stack/i.test(
            x.message
          )
        ) ||
        prev.items.find((x) => /Version\s+'/i.test(x.message));
      if (summaryCandidate) {
        prev.summaryMessage = summaryCandidate.message;
      }
      return;
    }

    const lastItem = prev ? prev.items[prev.items.length - 1] : null;
    const gapMs =
      lastItem && lastItem.tsMs != null && item.tsMs != null
        ? Math.abs(item.tsMs - lastItem.tsMs)
        : 0;
    const isSameOrEscherTag =
      prev &&
      (prev.tag === item.tag ||
        (/^Escher-/i.test(prev.tag) && /^Escher-/i.test(item.tag)));
    const prevExtraSig = lastItem ? (lastItem.extraLines || []).join('\n') : '';
    const itemExtraSig = (item.extraLines || []).join('\n');

    if (
      prev &&
      !prev.isCrashDumpGroup &&
      !item.isCrashDump &&
      prev.source === item.source &&
      isSameOrEscherTag &&
      prev.levelAbbr === item.levelAbbr &&
      prev.semKey === semKey &&
      prev.msgTemplate === item.msgTemplate &&
      prevExtraSig === itemExtraSig &&
      gapMs <= 2000 &&
      semKey !== 'crash_signal'
    ) {
      prev.items.push(item);
      prev.endDeltaLabel = item.deltaLabel;
    } else {
      groups.push({
        groupKey: `${item.source}-${item.index}-${groups.length}`,
        source: item.source,
        tag: item.tag,
        levelAbbr: item.levelAbbr,
        levelRank: item.levelRank,
        semKey,
        msgTemplate: item.msgTemplate,
        isCrashDumpGroup: !!item.isCrashDump,
        summaryMessage: item.message,
        semantic: item.semantic,
        startDeltaLabel: item.deltaLabel,
        endDeltaLabel: item.deltaLabel,
        items: [item],
      });
    }
  });

  const sourceCounts = {
    sysLog: merged.filter((x) => x.source === 'sysLog').length,
    userLog: merged.filter((x) => x.source === 'userLog').length,
    extraMessage: merged.filter((x) => x.source === 'extraMessage').length,
    martianLog: merged.filter((x) => x.source === 'martianLog').length,
  };

  return {
    totalRawCount: merged.length,
    filteredCount: filtered.length,
    hiddenEarlierCount,
    hiddenEarlierSysLogCount,
    anchorMs,
    autoExpandedWindow,
    sourceCounts,
    snapshotKvs,
    groups,
  };
}

const CrashEventTimeline = ({
  pid,
  sysLogs,
  userLogs,
  attachList,
  allowSysLog,
  allowUserLog,
  crashTime,
  logLevel,
  logKey,
  expName,
  expMessage,
}) => {
  const [windowSec, setWindowSec] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});

  const timelineResult = useMemo(
    () =>
      buildCrashTimelineEvents({
        sysLogs,
        userLogs,
        attachList,
        allowSysLog,
        allowUserLog,
        crashTime,
        keyword: logKey,
        minLevelName: logLevel,
        windowSec,
        categoryFilter,
      }),
    [
      sysLogs,
      userLogs,
      attachList,
      allowSysLog,
      allowUserLog,
      crashTime,
      logKey,
      logLevel,
      windowSec,
      categoryFilter,
    ]
  );

  const {
    totalRawCount,
    filteredCount,
    hiddenEarlierCount = 0,
    hiddenEarlierSysLogCount = 0,
    autoExpandedWindow,
    sourceCounts,
    snapshotKvs = [],
    groups,
  } = timelineResult;
  const isPcPlatform = Number(pid) === 10 || Number(pid) === 12 || Number(pid) === 20;

  return (
    <div
      data-testid="crash-event-timeline"
      style={{
        marginTop: '10px',
        padding: '12px 16px',
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: '6px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '12px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#262626' }}>
            {isPcPlatform
              ? ze('⚡ PC 崩溃前事件时间轴', '⚡ PC Pre-Crash Event Timeline')
              : ze('⚡ Android 崩溃前事件时间轴', '⚡ Android Pre-Crash Event Timeline')}
          </span>
          <Tag color="geekblue" style={{ margin: 0 }}>
            {ze(
              `展示 ${filteredCount} / ${totalRawCount} 条事件（${groups.length} 个节点）`,
              `${filteredCount} / ${totalRawCount} events (${groups.length} nodes)`
            )}
          </Tag>
          {sourceCounts.sysLog > 0 && (
            <Tag
              color={categoryFilter === 'sysLog' ? 'geekblue' : 'default'}
              style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}
              onClick={() =>
                setCategoryFilter((prev) => (prev === 'sysLog' ? 'all' : 'sysLog'))
              }
            >
              {isPcPlatform
                ? ze(`基础日志 ${sourceCounts.sysLog}`, `System Log ${sourceCounts.sysLog}`)
                : ze(`系统 logcat (Logcat) ${sourceCounts.sysLog}`, `System Log (Logcat) ${sourceCounts.sysLog}`)}
            </Tag>
          )}
          {sourceCounts.userLog > 0 && (
            <Tag
              color="blue"
              style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}
              onClick={() =>
                setCategoryFilter((prev) =>
                  prev === 'custom_engine' ? 'all' : 'custom_engine'
                )
              }
            >
              {isPcPlatform
                ? ze(`自定义日志 PrintLog.log ${sourceCounts.userLog}`, `PrintLog ${sourceCounts.userLog}`)
                : ze(`自定义日志 ${sourceCounts.userLog}`, `Custom ${sourceCounts.userLog}`)}
            </Tag>
          )}
          {sourceCounts.extraMessage > 0 && (
            <Tag
              color="purple"
              style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}
              onClick={() =>
                setCategoryFilter((prev) =>
                  prev === 'custom_engine' ? 'all' : 'custom_engine'
                )
              }
            >
              {isPcPlatform
                ? ze(`引擎回调 ExtraMessage.txt ${sourceCounts.extraMessage}`, `ExtraMessage ${sourceCounts.extraMessage}`)
                : ze(`引擎回调日志 ${sourceCounts.extraMessage}`, `Engine ${sourceCounts.extraMessage}`)}
            </Tag>
          )}
          {sourceCounts.martianLog > 0 && (
            <Tag
              color="cyan"
              style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}
              onClick={() =>
                setCategoryFilter((prev) =>
                  prev === 'custom_engine' ? 'all' : 'custom_engine'
                )
              }
            >
              {ze(`前后台切换 ${sourceCounts.martianLog}`, `Lifecycle ${sourceCounts.martianLog}`)}
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Radio.Group
            size="small"
            value={windowSec}
            onChange={(e) => setWindowSec(e.target.value)}
          >
            <Radio.Button value={30}>{ze('崩溃前 30 秒', 'Last 30s')}</Radio.Button>
            <Radio.Button value={120}>{ze('崩溃前 2 分钟', 'Last 2m')}</Radio.Button>
            <Radio.Button value={0}>{ze('全部时间', 'All Time')}</Radio.Button>
          </Radio.Group>
          <Radio.Group
            size="small"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <Radio.Button value="all">{ze('全部来源', 'All')}</Radio.Button>
            {sourceCounts.sysLog > 0 && (
              <Radio.Button value="sysLog">
                {isPcPlatform ? ze('基础日志', 'System Log') : ze('系统 logcat', 'System Log')}
              </Radio.Button>
            )}
            <Radio.Button value="custom_engine">
              {ze('自定义/引擎/前后台', 'Custom & Engine')}
            </Radio.Button>
            <Radio.Button value="signals">{ze('关键信号', 'Key Signals')}</Radio.Button>
            <Radio.Button value="warn_error">{ze('仅告警/错误', 'Warn / Error')}</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      {snapshotKvs.length > 0 && (
        <div
          data-testid="crash-event-timeline-snapshot"
          style={{
            marginBottom: '10px',
            padding: '6px 10px',
            background: '#ffffff',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            fontSize: '12px',
          }}
        >
          <span style={{ fontWeight: 600, color: '#595959' }}>
            {ze('📌 崩溃现场快照 (CustomizedData.txt):', '📌 Crash Scene Snapshot:')}
          </span>
          {snapshotKvs.map((kv) => (
            <Tag key={kv.key} color={kv.color} style={{ margin: 0, fontSize: '11px' }}>
              {ze(kv.labelZh, kv.labelEn)}
            </Tag>
          ))}
        </div>
      )}

      {hiddenEarlierCount > 0 && (
        <div
          data-testid="crash-event-timeline-earlier-banner"
          style={{
            marginBottom: '8px',
            padding: '5px 10px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#ad6800',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <span>
            {ze(
              `↑ 当前按「崩溃前 ${windowSec === 30 ? '30 秒' : '2 分钟'}」窗口筛选，已折叠更早的 ${hiddenEarlierCount} 条事件${
                hiddenEarlierSysLogCount > 0 ? `（含系统 logcat ${hiddenEarlierSysLogCount} 条）` : ''
              }`,
              `↑ ${hiddenEarlierCount} earlier events outside the ${windowSec}s window are hidden`
            )}
          </span>
          <a onClick={() => setWindowSec(0)}>
            {ze('点击展开全部时间 ▾', 'Show All Time ▾')}
          </a>
        </div>
      )}

      {autoExpandedWindow && (
        <div
          style={{
            marginBottom: '8px',
            padding: '4px 10px',
            background: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0050b3',
          }}
        >
          {ze(
            `崩溃前 ${windowSec} 秒内无日志记录，已自动为您展示崩溃前最近的 ${filteredCount} 条历史事件。`,
            `No logs within ${windowSec}s before crash; showing the latest ${filteredCount} events.`
          )}
        </div>
      )}

      {groups.length === 0 ? (
        <div style={{ padding: '16px 0', color: '#8c8c8c', fontSize: '12px' }}>
          {ze(
            '当前上报或筛选条件下暂无可展示的时间轴事件，可切换至「全部来源」或「原始日志」查看。',
            'No timeline events under current filter.'
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {groups.map((grp) => {
            const first = grp.items[0];
            const count = grp.items.length;
            const hasExtraLines = (first.extraLines || []).length > 0;
            const isExpanded = !!expandedGroups[grp.groupKey];
            const srcMeta = SOURCE_META[grp.source] || SOURCE_META.sysLog;
            const borderColor =
              grp.levelRank >= 5
                ? '#ffccc7'
                : grp.levelRank >= 3
                ? '#ffe58f'
                : grp.semantic
                ? '#d6e4ff'
                : '#f0f0f0';
            const bgColor =
              grp.levelRank >= 5
                ? '#fff2f0'
                : grp.levelRank >= 3
                ? '#fffbe6'
                : '#ffffff';

            return (
              <div
                key={grp.groupKey}
                style={{
                  padding: '6px 10px',
                  background: bgColor,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  <Tooltip title={first.timeText}>
                    <Tag
                      color={grp.levelRank >= 5 ? 'red' : 'default'}
                      style={{ margin: 0, fontFamily: 'monospace', fontWeight: 600 }}
                    >
                      {count > 1 && grp.startDeltaLabel !== grp.endDeltaLabel
                        ? `${grp.startDeltaLabel} ~ ${grp.endDeltaLabel}`
                        : grp.startDeltaLabel}
                    </Tag>
                  </Tooltip>
                  <Tag
                    color={LEVEL_COLOR[grp.levelAbbr] || 'default'}
                    style={{ margin: 0, fontSize: '11px' }}
                  >
                    {LEVEL_LABEL[grp.levelAbbr] || grp.levelAbbr}
                  </Tag>
                  <Tag color={srcMeta.color} style={{ margin: 0, fontSize: '11px' }}>
                    {ze(srcMeta.zh, srcMeta.en)}
                  </Tag>
                  {grp.semantic && (
                    <Tag color={grp.semantic.color} style={{ margin: 0, fontSize: '11px' }}>
                      {ze(grp.semantic.labelZh, grp.semantic.labelEn)}
                    </Tag>
                  )}
                  <span style={{ fontWeight: 600, color: '#434343' }}>{grp.tag}</span>
                  <span
                    style={{
                      color: '#262626',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      flex: 1,
                    }}
                  >
                    {grp.isCrashDumpGroup && count > 1
                      ? `${grp.summaryMessage || first.message} (${ze('已折叠崩溃转储明细', 'Crash dump folded')})`
                      : first.message}
                  </span>
                  {(count > 1 || hasExtraLines) && (
                    <a
                      style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                      onClick={() =>
                        setExpandedGroups((prev) => ({
                          ...prev,
                          [grp.groupKey]: !prev[grp.groupKey],
                        }))
                      }
                    >
                      {isExpanded
                        ? ze('收起详情 ▴', 'Collapse ▴')
                        : grp.isCrashDumpGroup && count > 1
                        ? ze(`展开崩溃转储明细 (${count} 行) ▾`, `Expand crash dump (${count} lines) ▾`)
                        : count > 1 && hasExtraLines
                        ? ze(
                            `连续 ${count} 条同类事件 · 附带堆栈 ${first.extraLines.length} 行 ▾`,
                            `${count} similar · ${first.extraLines.length} stack lines ▾`
                          )
                        : count > 1
                        ? ze(`连续 ${count} 条同类事件 ▾`, `${count} similar events ▾`)
                        : ze(`展开附带堆栈 (${first.extraLines.length} 行) ▾`, `Expand (${first.extraLines.length} lines) ▾`)}
                    </a>
                  )}
                </div>
                {isExpanded && (
                  <div
                    style={{
                      marginTop: '6px',
                      paddingTop: '6px',
                      borderTop: '1px dashed #e8e8e8',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                    }}
                  >
                    {hasExtraLines &&
                      first.extraLines.map((lineText, idx) => (
                        <div
                          key={`${grp.groupKey}-ext-${idx}`}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: '#595959',
                            paddingLeft: '12px',
                            wordBreak: 'break-all',
                          }}
                        >
                          {lineText}
                        </div>
                      ))}
                    {count > 1 &&
                      (grp.isCrashDumpGroup ? grp.items : grp.items.slice(1)).map((sub) => (
                        <div
                          key={`${grp.groupKey}-${sub.index}`}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: '#595959',
                            display: 'flex',
                            gap: '8px',
                          }}
                        >
                          <span style={{ color: '#8c8c8c', minWidth: '72px' }}>
                            {sub.deltaLabel}
                          </span>
                          <span style={{ wordBreak: 'break-all' }}>{sub.message}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          <div
            data-testid="crash-event-timeline-terminal"
            style={{
              marginTop: '4px',
              padding: '8px 12px',
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            <Tag color="red" style={{ margin: 0, fontFamily: 'monospace', fontWeight: 700 }}>
              T-0.00s
            </Tag>
            <span style={{ fontWeight: 700, color: '#cf1322', fontSize: '12px' }}>
              {ze('💥 异常发生 (Crash Triggered)', '💥 Crash Triggered')}
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#262626' }}>
              {`${expName || '-'}${expMessage && expMessage !== '-' ? ` — ${expMessage}` : ''}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

CrashEventTimeline.propTypes = {
  pid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sysLogs: PropTypes.array,
  userLogs: PropTypes.array,
  attachList: PropTypes.array,
  allowSysLog: PropTypes.bool,
  allowUserLog: PropTypes.bool,
  crashTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  logLevel: PropTypes.string,
  logKey: PropTypes.string,
  expName: PropTypes.string,
  expMessage: PropTypes.string,
};

export default CrashEventTimeline;
